import { Auth } from '@cadstrom/cadstrom-shared';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { MenuItemLink } from '#components/router-links/menu-item-link.js';
import { Toast } from '#components/toast/toast.js';
import { ConfirmationPopover } from '#screens/confirmation-popover/confirmation-popover.js';
import { useTRPC } from '#src/trpc.js';
import { Can } from '#state/contexts/ability-context.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { RenameProjectPopover } from './rename-project-popover.js';

export interface ProjectRowActionsMenuProps {
	projectId: string;
	projectName: string;
}

/**
 * Overflow (⋯) menu for a project row. Surfaces the demoted, non-primary
 * actions: open the project overview (anyone who can view), rename (project
 * managers), archive (staff only, behind a confirmation), and publish as a demo
 * (staff only, on the Cadstrom org's projects, behind a confirmation).
 */
export function ProjectRowActionsMenu({ projectId, projectName }: ProjectRowActionsMenuProps) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const [isRenameOpen, setIsRenameOpen] = useState(false);
	const [isArchiveOpen, setIsArchiveOpen] = useState(false);
	const [isPublishDemoOpen, setIsPublishDemoOpen] = useState(false);
	const [publishStarted, setPublishStarted] = useState(false);
	const [makeDefaultSample, setMakeDefaultSample] = useState(false);

	const { data: currentUser } = useQuery(trpc.user.getCurrent.queryOptions());
	const isStaff = currentUser?.auth.isStaff ?? false;

	const { orgName } = useParams({ strict: false });
	const canPublishDemo = isStaff && orgName === Auth.STAFF_ORG_USERNAME;

	const { data: publishStatus } = useQuery({
		...trpc.project.getDemoPublishStatus.queryOptions({ projectId }),
		enabled: canPublishDemo && publishStarted,
		refetchInterval: (query) => (query.state.data?.status === 'running' ? 2000 : false),
	});
	const isPublishing = publishStarted && publishStatus?.status === 'running';

	const { mutate: renameProject, isPending: isRenaming } = useMutation(
		trpc.project.update.mutationOptions({
			onSuccess: (data) => {
				void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
				addToast(
					<Toast severity='success' title='Project renamed'>
						{`"${projectName}" has been renamed to "${data.project.name}".`}
					</Toast>,
				);
				setIsRenameOpen(false);
			},
			onError: (error) => {
				addToast(
					<Toast severity='error' title='Failed to rename project'>
						{error instanceof Error ? error.message : 'An unexpected error occurred.'}
					</Toast>,
				);
			},
		}),
	);

	const { mutate: archiveProject, isPending: isArchiving } = useMutation(
		trpc.project.archive.mutationOptions({
			onSuccess: () => {
				void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
				addToast(
					<Toast severity='success' title='Project archived'>
						{`"${projectName}" has been archived.`}
					</Toast>,
				);
				setIsArchiveOpen(false);
			},
			onError: (error) => {
				addToast(
					<Toast severity='error' title='Failed to archive project'>
						{error instanceof Error ? error.message : 'An unexpected error occurred.'}
					</Toast>,
				);
			},
		}),
	);

	const { mutate: publishAsDemo, isPending: isPublishRequestPending } = useMutation(
		trpc.project.publishAsDemo.mutationOptions({
			onSuccess: (result) => {
				setIsPublishDemoOpen(false);
				setPublishStarted(true);
				// Re-fetch status so a repeat publish on this row restarts polling even
				// though publishStarted is already true.
				void queryClient.invalidateQueries({
					queryKey: trpc.project.getDemoPublishStatus.queryKey({ projectId }),
				});
				if (result.alreadyRunning) {
					addToast(
						<Toast severity='info' title='Already publishing'>
							{`A demo publish for "${projectName}" is already running.`}
						</Toast>,
					);
					return;
				}
				addToast(
					<Toast severity='success' title='Publishing demo'>
						{`"${projectName}" is being published to the demo catalogue in the background.`}
					</Toast>,
				);
			},
			onError: (error) => {
				addToast(
					<Toast severity='error' title='Failed to publish demo'>
						{error instanceof Error ? error.message : 'An unexpected error occurred.'}
					</Toast>,
				);
			},
		}),
	);

	return (
		<>
			{isPublishing && (
				<Tooltip title='Publishing as demo…'>
					<CircularProgress size={16} aria-label='Publishing as demo' />
				</Tooltip>
			)}
			<Tooltip title='More actions'>
				<IconButton
					size='small'
					aria-label='More actions'
					onClick={(event) => {
						setAnchorEl(event.currentTarget);
						setMenuOpen(true);
					}}
				>
					<MoreVertIcon fontSize='small' />
				</IconButton>
			</Tooltip>

			<Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setMenuOpen(false)}>
				<MenuItemLink
					from='/orgs/$orgName/projects/'
					to='./$projectId'
					params={{ projectId }}
					onClick={() => setMenuOpen(false)}
				>
					<ListItemIcon>
						<VisibilityOutlinedIcon fontSize='small' />
					</ListItemIcon>
					<ListItemText>Project overview</ListItemText>
				</MenuItemLink>

				<Can I='manage' a='project'>
					<MenuItem
						onClick={() => {
							setMenuOpen(false);
							setIsRenameOpen(true);
						}}
					>
						<ListItemIcon>
							<EditOutlinedIcon fontSize='small' />
						</ListItemIcon>
						<ListItemText>Rename</ListItemText>
					</MenuItem>
				</Can>

				{isStaff && <Divider />}
				{isStaff && (
					<MenuItem
						className='text-red-11'
						onClick={() => {
							setMenuOpen(false);
							setIsArchiveOpen(true);
						}}
					>
						<ListItemIcon>
							<ArchiveOutlinedIcon fontSize='small' className='text-red-11' />
						</ListItemIcon>
						<ListItemText>Archive</ListItemText>
					</MenuItem>
				)}

				{canPublishDemo && <Divider />}
				{canPublishDemo && (
					<MenuItem
						onClick={() => {
							setMenuOpen(false);
							setMakeDefaultSample(false);
							setIsPublishDemoOpen(true);
						}}
					>
						<ListItemIcon>
							<CloudUploadOutlinedIcon fontSize='small' />
						</ListItemIcon>
						<ListItemText>Publish as demo</ListItemText>
					</MenuItem>
				)}
			</Menu>

			<RenameProjectPopover
				currentName={projectName}
				visible={isRenameOpen && anchorEl !== null ? { anchorEl } : false}
				loading={isRenaming}
				onSubmit={(name) => renameProject({ id: projectId, data: { name } })}
				onCancel={() => setIsRenameOpen(false)}
			/>

			<ConfirmationPopover
				visible={isArchiveOpen && anchorEl !== null ? { anchorEl } : false}
				loading={isArchiving}
				onConfirm={() => archiveProject({ id: projectId })}
				onCancel={() => setIsArchiveOpen(false)}
			>
				<p className='max-w-72'>
					Archive <span className='font-bold'>{projectName}</span>? It will be removed from the projects list. The
					project and its data are preserved and can be restored by an administrator.
				</p>
			</ConfirmationPopover>

			<ConfirmationPopover
				visible={isPublishDemoOpen && anchorEl !== null ? { anchorEl } : false}
				loading={isPublishRequestPending}
				onConfirm={() => publishAsDemo({ projectId, makeDefault: makeDefaultSample })}
				onCancel={() => setIsPublishDemoOpen(false)}
			>
				<div className='flex max-w-72 flex-col gap-2'>
					<p>
						Publish <span className='font-bold'>{projectName}</span> as a demo? This exports the project into the shared
						demo catalogue, replacing any existing demo bundle for it. It runs in the background.
					</p>
					<FormControlLabel
						control={
							<Checkbox
								size='small'
								checked={makeDefaultSample}
								onChange={(event) => setMakeDefaultSample(event.target.checked)}
							/>
						}
						label='Make this the default sample project'
					/>
				</div>
			</ConfirmationPopover>
		</>
	);
}
